import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthUser {
  id: string;
  email: string;
  role: string;
  businessId?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = window.discoveryApi.getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      // Restore the stored user, then silently refresh the token on boot so a
      // stale (expired) token can't send the app into a failed-request loop.
      const storedUser = window.discoveryApi.getUser();
      setUser(storedUser);
      try {
        await window.discoveryApi.refreshToken();
        // Track session restoration for funnel analytics
        if (storedUser) {
          Promise.resolve(window.discoveryApi.trackFunnelEvent?.({ type: 'return_session', metadata: { returning_user: true } })).catch(() => undefined);
        }
      } catch {
        window.discoveryApi.clearSession();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const setSessionUser = () => {
    setUser(window.discoveryApi.getUser());
  };

  const login = async (email: string, password: string) => {
    const { user: sessionUser } = await window.discoveryApi.login(email, password);
    setUser(sessionUser);
  };

  const register = async (email: string, password: string) => {
    const { user: sessionUser } = await window.discoveryApi.register(email, password);
    setUser(sessionUser);
  };

  const logout = () => {
    window.discoveryApi.logout();
    setUser(null);
  };

  const refreshSession = async (): Promise<boolean> => {
    try {
      await window.discoveryApi.refreshToken();
      setSessionUser();
      return true;
    } catch {
      window.discoveryApi.clearSession();
      setUser(null);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
